(() => {
  const STORAGE_KEY = 'zasuwa_navigation_context';
  const PENDING_RESTORE_KEY = 'zasuwa_navigation_context_pending_restore';
  const MAX_HISTORY = 8;
  const INTERNAL_TYPES = new Set(['collection', 'product', 'search', 'home', 'featured', 'other']);

  const canUseStorage = () => {
    try {
      return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
    } catch (error) {
      return false;
    }
  };

  const storage = canUseStorage() ? window.sessionStorage : null;

  const safeParse = (value, fallback) => {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const cloneNode = (node) => {
    if (!node || typeof node !== 'object') return null;
    const type = INTERNAL_TYPES.has(node.type) ? node.type : 'other';
    const url = typeof node.url === 'string' ? node.url : '';
    const title = typeof node.title === 'string' ? node.title : '';
    const source = typeof node.source === 'string' ? node.source : '';
    const scrollY = Number.isFinite(Number(node.scrollY)) ? Math.max(0, Number(node.scrollY)) : 0;
    const timestamp = Number.isFinite(Number(node.timestamp)) ? Number(node.timestamp) : Date.now();
    return {
      type,
      url,
      title,
      scrollY,
      source,
      timestamp,
    };
  };

  const normalizeContext = (context) => {
    const history = Array.isArray(context?.history) ? context.history.map(cloneNode).filter(Boolean) : [];
    const normalized = {
      version: 1,
      history: trimHistory(history),
      lastSource: cloneNode(context?.lastSource),
    };

    if (!normalized.lastSource && normalized.history.length > 1) {
      normalized.lastSource = cloneNode(normalized.history[normalized.history.length - 2]);
    }

    return normalized;
  };

  const readContext = () => {
    if (!storage) {
      return normalizeContext({ version: 1, history: [] });
    }

    return normalizeContext(safeParse(storage.getItem(STORAGE_KEY), { version: 1, history: [] }));
  };

  const writeContext = (context) => {
    const normalized = normalizeContext(context);

    if (!storage) return normalized;

    storage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  };

  const trimHistory = (history) => {
    const trimmed = [];

    for (const entry of history) {
      const node = cloneNode(entry);
      if (!node) continue;

      const last = trimmed[trimmed.length - 1];
      if (last && sameNode(last, node)) {
        trimmed[trimmed.length - 1] = mergeNode(last, node);
        continue;
      }

      trimmed.push(node);
    }

    while (trimmed.length > MAX_HISTORY) {
      trimmed.shift();
    }

    return trimmed;
  };

  const mergeNode = (base, patch) => {
    const merged = cloneNode(base) || cloneNode(patch);
    if (!merged) return null;

    if (patch?.title) merged.title = patch.title;
    if (patch?.source) merged.source = patch.source;
    if (Number.isFinite(Number(patch?.scrollY))) merged.scrollY = Math.max(0, Number(patch.scrollY));
    if (Number.isFinite(Number(patch?.timestamp))) merged.timestamp = Number(patch.timestamp);
    if (patch?.url) merged.url = patch.url;
    if (patch?.type && INTERNAL_TYPES.has(patch.type)) merged.type = patch.type;

    return merged;
  };

  const getOriginUrl = () => {
    try {
      return new URL(window.location.href);
    } catch (error) {
      return null;
    }
  };

  const normalizeUrl = (url, type) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.hash = '';

      if (type === 'product') {
        parsed.searchParams.delete('variant');
      }

      return `${parsed.origin}${parsed.pathname}${parsed.search}`;
    } catch (error) {
      return url;
    }
  };

  const isSameOrigin = (url) => {
    if (!url) return false;

    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const inferTypeFromUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const pathname = parsed.pathname.replace(/\/+$/, '') || '/';

      if (pathname === '/' || pathname === '') return 'home';
      if (pathname.startsWith('/search')) return 'search';
      if (pathname.startsWith('/products/')) return 'product';
      if (pathname.startsWith('/collections/')) return 'collection';
      return 'other';
    } catch (error) {
      return 'other';
    }
  };

  const getPageType = () => {
    const body = document.body;
    if (!body) return 'other';

    if (body.classList.contains('template-product')) return 'product';
    if (body.classList.contains('template-collection')) return 'collection';
    if (body.classList.contains('template-search')) return 'search';
    if (body.classList.contains('template-index')) return 'home';

    return 'other';
  };

  const getTitleCandidates = () => {
    const body = document.body;
    if (!body) return [];

    return [
      'main h1',
      '.product__title h1',
      '.collection-hero__title',
      '.search__heading',
      '.main-page-title',
      '[data-page-title]',
    ];
  };

  const getPageTitle = (type) => {
    const candidates = getTitleCandidates();
    for (const selector of candidates) {
      const element = document.querySelector(selector);
      const text = element?.textContent?.trim();
      if (text) return text;
    }

    if (type === 'search') return 'Search results';
    if (type === 'home') return document.title.replace(/[\s|–-]+.*$/, '').trim() || 'Home';
    if (type === 'collection') return 'Collection';
    if (type === 'product') return 'Product';

    return document.title.replace(/[\s|–-]+.*$/, '').trim() || '';
  };

  const buildNode = (overrides = {}) => {
    const type = INTERNAL_TYPES.has(overrides.type) ? overrides.type : getPageType();
    const url = normalizeUrl(overrides.url || window.location.href, type);
    const title = overrides.title ?? getPageTitle(type);
    const source = overrides.source ?? '';
    const scrollY = Number.isFinite(Number(overrides.scrollY)) ? Math.max(0, Number(overrides.scrollY)) : window.scrollY || 0;
    const timestamp = Number.isFinite(Number(overrides.timestamp)) ? Number(overrides.timestamp) : Date.now();

    return cloneNode({ type, url, title, source, scrollY, timestamp });
  };

  const sameNode = (left, right) => {
    if (!left || !right) return false;
    return left.type === right.type && normalizeUrl(left.url, left.type) === normalizeUrl(right.url, right.type);
  };

  const findCurrentIndex = (history, node) => {
    for (let index = history.length - 1; index >= 0; index -= 1) {
      if (sameNode(history[index], node)) {
        return index;
      }
    }

    return -1;
  };

  const seedReferrerNode = (context, currentNode) => {
    if (context.history.length > 0) return context;
    if (!document.referrer || !isSameOrigin(document.referrer)) return context;

    const referrerType = inferTypeFromUrl(document.referrer);
    const referrerNode = buildNode({
      type: referrerType,
      url: document.referrer,
      title: referrerType === 'search' ? 'Search results' : referrerType === 'home' ? 'Home' : referrerType === 'collection' ? 'Collection' : referrerType === 'product' ? 'Product' : 'Back',
      scrollY: 0,
      source: 'referrer',
      timestamp: Date.now() - 1,
    });

    if (!sameNode(referrerNode, currentNode)) {
      context.history.push(referrerNode);
    }

    return context;
  };

  const setLastSource = (context) => {
    if (context.history.length > 1) {
      context.lastSource = cloneNode(context.history[context.history.length - 2]);
    } else {
      context.lastSource = null;
    }
  };

  const getPreviousCollectionNode = () => {
    const context = readContext();
    if (context.history.length < 2) return null;

    const previousNode = context.history[context.history.length - 2];
    if (previousNode?.type === 'collection' && isSameOrigin(previousNode.url)) {
      return cloneNode(previousNode);
    }

    return null;
  };

  const getCollectionHandleFromUrl = (url) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/collections\/([^/]+)/i);
      if (!match) return '';

      return decodeURIComponent(match[1]);
    } catch (error) {
      return '';
    }
  };

  const getProductCollectionHandles = (collectionLink) => {
    const rawCollections = collectionLink?.dataset?.productCollections || '';

    try {
      const parsedCollections = JSON.parse(rawCollections);
      if (Array.isArray(parsedCollections)) {
        return parsedCollections
          .map((entry) => ({
            handle: String(entry?.handle || '').trim().toLowerCase(),
            title: String(entry?.title || '').trim(),
            url: String(entry?.url || '').trim(),
          }))
          .filter((entry) => entry.handle || entry.title || entry.url);
      }
    } catch (error) {
      // Fall through to legacy handle parsing.
    }

    const rawHandles = collectionLink?.dataset?.productCollectionHandles || '';

    return rawHandles
      .split('|')
      .map((handle) => handle.trim())
      .filter(Boolean)
      .map((handle) => ({ handle: handle.toLowerCase(), title: '', url: '' }));
  };

  const isExcludedNavigationCollectionTitle = (title) => {
    const normalizedTitle = String(title || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    if (!normalizedTitle) return true;

    if (normalizedTitle === 'sale') return true;
    if (normalizedTitle === 'best sellers') return true;
    if (normalizedTitle === 'collection') return true;
    if (normalizedTitle === 'collections') return true;
    if (normalizedTitle === '促销集合') return true;
    if (normalizedTitle === '自动集合') return true;
    if (/^\$\d+.*off/.test(normalizedTitle)) return true;

    return false;
  };

  const isAllNavigationCollectionTitle = (title) => {
    return String(title || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .startsWith('all ');
  };

  const getHeaderNavigationRoot = () => {
    return document.querySelector('.header__inline-menu') || document.querySelector('.menu-drawer__navigation');
  };

  const normalizeNavigationKey = (value) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase();

    if (!normalized) return '';

    const unicodeNormalized = typeof normalized.normalize === 'function' ? normalized.normalize('NFKD') : normalized;

    return unicodeNormalized
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getNavigationTaxonomyData = (() => {
    let cachedTaxonomy = null;

    return () => {
      if (cachedTaxonomy) return cachedTaxonomy;

      const script = document.querySelector('[data-navigation-taxonomy]');
      if (!script) {
        cachedTaxonomy = { menuHandle: '', nodes: [], byType: {} };
        return cachedTaxonomy;
      }

      const raw = script.textContent?.trim();
      if (!raw) {
        cachedTaxonomy = { menuHandle: '', nodes: [], byType: {} };
        return cachedTaxonomy;
      }

      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        cachedTaxonomy = { menuHandle: '', nodes: [], byType: {} };
        return cachedTaxonomy;
      }

      const nodes = Array.isArray(parsed?.nodes) ? parsed.nodes : [];
      const byType = {};

      for (const node of nodes) {
        if (!node || typeof node !== 'object') continue;

        const key = normalizeNavigationKey(node.key || node.title);
        if (!key) continue;

        if (!byType[key]) {
          byType[key] = [];
        }

        byType[key].push({
          title: typeof node.title === 'string' ? node.title : '',
          url: typeof node.url === 'string' ? node.url : '',
          key,
          depth: Number(node.depth) || 0,
          parent: node.parent && typeof node.parent === 'object' ? node.parent : null,
          ancestors: Array.isArray(node.ancestors) ? node.ancestors : [],
        });
      }

      cachedTaxonomy = {
        menuHandle: typeof parsed?.menuHandle === 'string' ? parsed.menuHandle : '',
        nodes,
        byType,
      };

      return cachedTaxonomy;
    };
  })();

  const getBestTaxonomyNode = (nodes) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    return nodes
      .slice()
      .sort((left, right) => {
        const leftDepth = Number(left?.depth) || 0;
        const rightDepth = Number(right?.depth) || 0;

        if (rightDepth !== leftDepth) return rightDepth - leftDepth;
        return 0;
      })[0] || null;
  };

  const getNavigationTaxonomyFallback = (collectionLink) => {
    if (!collectionLink) return null;

    const typeKey = normalizeNavigationKey(collectionLink.dataset.productType || '');
    if (!typeKey) return null;

    const taxonomy = getNavigationTaxonomyData();
    const matchedNodes = taxonomy.byType?.[typeKey];
    const selectedNode = getBestTaxonomyNode(matchedNodes);

    if (!selectedNode || !selectedNode.url) return null;

    return selectedNode;
  };

  const getNavigationTaxonomyPathLabel = (node) => {
    if (!node) return '';

    const ancestors = Array.isArray(node.ancestors) ? node.ancestors : [];
    const pathTitles = ancestors
      .map((ancestor) => String(ancestor?.title || '').trim())
      .filter(Boolean);

    const leafTitle = String(node.title || '').trim();
    if (leafTitle) {
      pathTitles.push(leafTitle);
    }

    return pathTitles.join(' / ');
  };

  const getNavigationBreadcrumbContainer = () => {
    return document.querySelector('[data-navigation-taxonomy-links]');
  };

  const clearElement = (element) => {
    if (!element) return;
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
  };

  const isValidNavigationBreadcrumbUrl = (url) => {
    const rawHref = String(url || '').trim();
    if (!rawHref) return false;
    if (rawHref.startsWith('#')) return false;
    return true;
  };

  const createBreadcrumbLink = (node, options = {}) => {
    if (!node || !node.url) return null;

    const link = document.createElement('a');
    link.className = 'product-quick-nav__link caption-with-letter-spacing';
    link.href = node.url;
    link.textContent = node.title || '';

    if (options.restoreScroll) {
      link.setAttribute('data-navigation-restore-scroll', 'true');
      link.setAttribute('data-navigation-scroll-y', String(Number(options.scrollY) || 0));
      link.setAttribute('data-navigation-fallback-title', options.fallbackTitle || node.title || 'Collection');
      link.setAttribute('aria-label', 'Back to ' + (options.ariaLabel || getNavigationFallbackLabel(node)));
    }

    return link;
  };

  const renderBreadcrumbLinks = (container, nodes, options = {}) => {
    if (!container) return;

    clearElement(container);

    const validNodes = Array.isArray(nodes)
      ? nodes
          .map((node) => ({
            title: String(node?.title || '').trim(),
            url: String(node?.url || '').trim(),
          }))
          .filter((node) => node.title && isValidNavigationBreadcrumbUrl(node.url))
      : [];

    if (validNodes.length === 0) {
      container.hidden = true;
      container.setAttribute('aria-hidden', 'true');
      return 0;
    }

    validNodes.forEach((node, index) => {
      const link = createBreadcrumbLink(node, options.linkOptions || {});
      if (!link) return;

      container.appendChild(link);

      if (index < validNodes.length - 1) {
        const separator = document.createElement('span');
        separator.className = 'product-quick-nav__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '/';
        container.appendChild(separator);
      }
    });

    container.hidden = false;
    container.removeAttribute('aria-hidden');
    container.removeAttribute('tabindex');
    return validNodes.length;
  };

  const getNavigationCollectionFallback = (collectionLink) => {
    const navigationRoot = getHeaderNavigationRoot();
    if (!navigationRoot) return null;

    const productCollections = getProductCollectionHandles(collectionLink);
    if (productCollections.length === 0) return null;

    const productCollectionHandles = new Set(
      productCollections.map((productCollection) => String(productCollection?.handle || '').toLowerCase()).filter(Boolean)
    );
    const rawCandidates = [];
    const anchors = navigationRoot.querySelectorAll('a[href]');

    anchors.forEach((anchor, order) => {
      const handle = getCollectionHandleFromUrl(anchor.getAttribute('href'));
      const title = anchor.textContent.replace(/\s+/g, ' ').trim();
      if (!handle || !productCollectionHandles.has(handle.toLowerCase())) return;

      let depth = 0;
      let current = anchor.parentElement;

      while (current && current !== navigationRoot) {
        if (current.tagName === 'UL') depth += 1;
        current = current.parentElement;
      }

      rawCandidates.push({
        depth,
        handle,
        href: anchor.href,
        order,
        title,
      });
    });

    console.log('[QuickNav] raw candidates', rawCandidates);

    const filteredCandidates = rawCandidates.filter((candidate) => !isExcludedNavigationCollectionTitle(candidate.title));
    console.log('[QuickNav] after exclusion', filteredCandidates);

    if (filteredCandidates.length === 0) return null;

    const dedupedCandidates = [];
    const dedupeByHandle = new Map();

    for (const candidate of filteredCandidates) {
      const key = String(candidate.handle || '').toLowerCase();
      if (!key) continue;

      const existing = dedupeByHandle.get(key);
      if (!existing) {
        dedupeByHandle.set(key, candidate);
        dedupedCandidates.push(candidate);
        continue;
      }

      if (candidate.depth > existing.depth || (candidate.depth === existing.depth && candidate.order < existing.order)) {
        const existingIndex = dedupedCandidates.findIndex((entry) => String(entry.handle || '').toLowerCase() === key);
        if (existingIndex >= 0) {
          dedupedCandidates[existingIndex] = candidate;
        }
        dedupeByHandle.set(key, candidate);
      }
    }

    console.log('[QuickNav] after dedupe', dedupedCandidates);

    dedupedCandidates.sort((left, right) => {
      const leftIsAll = isAllNavigationCollectionTitle(left.title);
      const rightIsAll = isAllNavigationCollectionTitle(right.title);

      if (leftIsAll !== rightIsAll) {
        return leftIsAll ? 1 : -1;
      }

      if (right.depth !== left.depth) return right.depth - left.depth;
      return left.order - right.order;
    });

    const selectedCandidate = dedupedCandidates[0] || null;
    console.log('[QuickNav] selected candidate', selectedCandidate);
    return selectedCandidate;
  };

  const getNavigationFallbackLabel = (node) => {
    if (!node) return 'Back';
    if (node.title && node.title.length <= 42) return node.title;

    if (node.type === 'search') return 'Search results';
    if (node.type === 'home') return 'Home';
    if (node.type === 'collection') return 'Collection';
    if (node.type === 'product') return 'Previous product';

    return 'Back';
  };

  const pushNode = (node) => {
    const context = readContext();
    const nextNode = buildNode(node);

    if (!nextNode) return context;

    const last = context.history[context.history.length - 1];
    if (last && sameNode(last, nextNode)) {
      context.history[context.history.length - 1] = mergeNode(last, nextNode);
    } else {
      context.history.push(nextNode);
    }

    context.history = trimHistory(context.history);
    setLastSource(context);
    return writeContext(context);
  };

  const updateCurrentNode = (patch = {}) => {
    const context = readContext();
    const currentIndex = context.history.length - 1;
    const currentNode = buildNode({ ...patch, type: patch.type || getPageType() });

    if (!currentNode) return context;

    if (currentIndex >= 0) {
      context.history[currentIndex] = mergeNode(context.history[currentIndex], currentNode) || currentNode;
    } else {
      context.history.push(currentNode);
    }

    context.history = trimHistory(context.history);
    setLastSource(context);
    return writeContext(context);
  };

  const getPreviousNode = () => {
    const context = readContext();
    if (context.history.length < 2) return null;
    return cloneNode(context.history[context.history.length - 2]);
  };

  const savePendingRestore = (node) => {
    const restoreNode = buildNode({
      type: node?.type || inferTypeFromUrl(node?.url || window.location.href),
      url: node?.url || window.location.href,
      title: node?.title || '',
      scrollY: Number.isFinite(Number(node?.scrollY)) ? Number(node.scrollY) : 0,
      source: node?.source || '',
      timestamp: Date.now(),
    });

    if (!restoreNode || !storage) return restoreNode;

    storage.setItem(PENDING_RESTORE_KEY, JSON.stringify(restoreNode));
    return restoreNode;
  };

  const restorePendingScroll = () => {
    if (!storage) return false;

    const pending = cloneNode(safeParse(storage.getItem(PENDING_RESTORE_KEY), null));
    if (!pending) return false;

    const current = buildNode({ type: getPageType(), url: window.location.href, title: getPageTitle(getPageType()), scrollY: window.scrollY || 0 });
    if (!sameNode(pending, current)) return false;

    const scrollTarget = Math.max(0, pending.scrollY || 0);
    storage.removeItem(PENDING_RESTORE_KEY);

    const applyScroll = () => {
      window.scrollTo({ top: scrollTarget, left: 0, behavior: 'auto' });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(applyScroll);
    });

    window.setTimeout(applyScroll, 48);
    return true;
  };

  const updateSmartBackButton = () => {
    const button = document.querySelector('[data-smart-back]');
    if (!button) return;

    const previous = getPreviousNode();
    if (!previous || !isSameOrigin(previous.url) || previous.type === 'collection') {
      button.hidden = true;
      button.removeAttribute('aria-label');
      button.removeAttribute('data-back-url');
      button.textContent = '\u2190 Back';
      return;
    }

    const label = getNavigationFallbackLabel(previous);

    button.hidden = false;
    button.dataset.backUrl = previous.url;
    button.setAttribute('aria-label', 'Back to ' + label);
    button.textContent = '\u2190 ' + label;
  };

  const updateNavigationLinks = () => {
    const breadcrumbContainer = getNavigationBreadcrumbContainer();
    if (!breadcrumbContainer) return;

    breadcrumbContainer.removeAttribute('data-navigation-restore-scroll');
    breadcrumbContainer.removeAttribute('data-navigation-scroll-y');
    breadcrumbContainer.removeAttribute('aria-label');
    breadcrumbContainer.removeAttribute('data-navigation-taxonomy-key');
    breadcrumbContainer.removeAttribute('data-navigation-taxonomy-depth');

    const taxonomyFallback = getNavigationTaxonomyFallback(breadcrumbContainer);
    if (taxonomyFallback) {
      const breadcrumbNodes = [...(taxonomyFallback.ancestors || []), taxonomyFallback]
        .map((node) => ({
          title: String(node?.title || '').trim(),
          url: String(node?.url || '').trim(),
        }))
        .filter((node) => node.title && isValidNavigationBreadcrumbUrl(node.url));

      const renderedCount = renderBreadcrumbLinks(breadcrumbContainer, breadcrumbNodes);
      if (renderedCount > 0) {
        breadcrumbContainer.setAttribute('data-navigation-taxonomy-key', taxonomyFallback.key || '');
        breadcrumbContainer.setAttribute('data-navigation-taxonomy-depth', String(taxonomyFallback.depth || 0));
        return;
      }

      breadcrumbContainer.removeAttribute('data-navigation-taxonomy-key');
      breadcrumbContainer.removeAttribute('data-navigation-taxonomy-depth');
      return;
    }

    clearElement(breadcrumbContainer);
    breadcrumbContainer.hidden = true;
    breadcrumbContainer.setAttribute('aria-hidden', 'true');
    breadcrumbContainer.removeAttribute('data-navigation-taxonomy-key');
    breadcrumbContainer.removeAttribute('data-navigation-taxonomy-depth');
  };

  const syncLoadCurrentPage = () => {
    const context = readContext();
    const currentNode = buildNode();
    const existingIndex = findCurrentIndex(context.history, currentNode);

    if (existingIndex >= 0) {
      context.history = context.history.slice(0, existingIndex + 1);
      context.history[existingIndex] = mergeNode(context.history[existingIndex], currentNode) || currentNode;
    } else {
      seedReferrerNode(context, currentNode);
      context.history.push(currentNode);
    }

    context.history = trimHistory(context.history);
    setLastSource(context);
    writeContext(context);
    updateNavigationLinks();
    updateSmartBackButton();
    return context;
  };

  const syncMutatedPage = () => {
    const context = readContext();
    const currentNode = buildNode();
    const lastIndex = context.history.length - 1;

    if (lastIndex >= 0) {
      context.history[lastIndex] = mergeNode(context.history[lastIndex], currentNode) || currentNode;
    } else {
      context.history.push(currentNode);
    }

    context.history = trimHistory(context.history);
    setLastSource(context);
    writeContext(context);
    updateNavigationLinks();
    updateSmartBackButton();
    return context;
  };

  const captureInternalNavigation = (event) => {
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) return;
    if (anchor.hasAttribute('data-smart-back')) return;
    if (anchor.target && anchor.target !== '_self') return;
    if (anchor.hasAttribute('download')) return;

    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

    let parsed;
    try {
      parsed = new URL(rawHref, window.location.href);
    } catch (error) {
      return;
    }

    if (parsed.origin !== window.location.origin) return;

    if (anchor.hasAttribute('data-navigation-restore-scroll')) {
      savePendingRestore({
        type: 'collection',
        url: anchor.href,
        title: anchor.dataset.fallbackTitle || anchor.textContent?.replace(/^\u2039\s*/, '').trim() || '',
        scrollY: Number(anchor.getAttribute('data-navigation-scroll-y')) || 0,
        source: 'collection-link',
      });
    }

    updateCurrentNode({
      scrollY: window.scrollY || 0,
      timestamp: Date.now(),
    });
  };

  const handleSmartBackClick = (event) => {
    const button = event.target?.closest?.('[data-smart-back]');
    if (!button) return;

    event.preventDefault();

    const previous = getPreviousNode();
    if (!previous || !isSameOrigin(previous.url)) {
      if (window.history.length > 1 && document.referrer && isSameOrigin(document.referrer)) {
        window.history.back();
        return;
      }

      if (window.routes?.root_url) {
        window.location.assign(window.routes.root_url);
        return;
      }

      window.location.assign('/');
      return;
    }

    if (previous.type === 'collection' || previous.type === 'search') {
      savePendingRestore(previous);
    }

    window.location.assign(previous.url);
  };

  const handleHistoryMutation = () => {
    syncMutatedPage();
  };

  const patchHistoryMethod = (methodName) => {
    const original = window.history[methodName];
    if (typeof original !== 'function') return;
    if (original.__navigationContextPatched) return;

    const patched = function patchedHistoryMethod(...args) {
      const result = original.apply(this, args);
      window.requestAnimationFrame(handleHistoryMutation);
      return result;
    };

    patched.__navigationContextPatched = true;
    window.history[methodName] = patched;
  };

  const bindEvents = () => {
    if (window.__navigationContextBound) return;
    window.__navigationContextBound = true;

    document.addEventListener('click', captureInternalNavigation, true);
    document.addEventListener('click', handleSmartBackClick, true);
    window.addEventListener('pageshow', () => {
      syncLoadCurrentPage();
      restorePendingScroll();
    });
    document.addEventListener('DOMContentLoaded', () => {
      syncLoadCurrentPage();
      restorePendingScroll();
    });
    window.addEventListener('pagehide', () => {
      updateCurrentNode({
        scrollY: window.scrollY || 0,
        timestamp: Date.now(),
      });
    });
    window.addEventListener('beforeunload', () => {
      updateCurrentNode({
        scrollY: window.scrollY || 0,
        timestamp: Date.now(),
      });
    });
  };

  const init = () => {
    patchHistoryMethod('pushState');
    patchHistoryMethod('replaceState');
    bindEvents();
    syncLoadCurrentPage();
    restorePendingScroll();
    updateNavigationLinks();
    updateSmartBackButton();
  };

  window.NavigationContext = {
    readContext,
    writeContext,
    pushNode,
    updateCurrentNode,
    getPreviousNode,
    savePendingRestore,
    restorePendingScroll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
